/**
 * Ground-truth attribution for URL-referred signups.
 *
 * The StoryForge comic's report card links to /learn/{slug}?src=storyforge.
 * The GA session join answers behavior questions, but revenue-per-source has
 * to come from the database: this stash carries the param from the landing
 * URL to registration, where the server writes the set-once
 * users.signup_source column.
 *
 * The server's SignupSource enum is the SINGLE authority on which values mean
 * anything (Codex P2 on #823 — a value list duplicated here would drift: the
 * next server-side source would be silently dropped before registration).
 * The client checks only two things:
 *
 *  - FORMAT: slug-shaped, so localStorage never holds crafted junk — the
 *    referral stash's format-not-value rule (services/referral.service.ts).
 *    An unknown-but-well-formed value rides to the server and sanitizes to
 *    NULL there; fail-open, analytics-grade.
 *  - guest_tour specifically is refused: it is a product-flow fact the auth
 *    store asserts itself, and accepting it by URL would let a pasted link
 *    forge guest-tour attribution.
 *
 * First touch wins — an existing stash is never overwritten; attribution
 * that can change later is worthless. Failures (Safari private mode) degrade
 * to no attribution, never to a broken page.
 */
const KEY = 'signup_source_hint';

const SOURCE_FORMAT = /^[a-z][a-z0-9_]{0,31}$/;

function isCarriable(value: string): boolean {
  return SOURCE_FORMAT.test(value) && value !== 'guest_tour';
}

/**
 * Stash FIRST, unconditionally — even when a persisted token exists. A
 * persisted token can be expired or revoked, and refusing to stash on that
 * guess loses a legitimate arrival's attribution (Codex #823 r5). The
 * signed-in case is handled where it is actually KNOWN: every VALIDATED
 * authentication (the boot /me included) routes through setAuthState with
 * isAuthenticated:true, which spends the hint. So a genuinely signed-in
 * browser stashes for a few seconds and the validation clears it; a browser
 * with a dead token keeps the hint for the registration it is about to make.
 */
export function stashSignupSourceFromUrl(search: string): void {
  try {
    const src = new URLSearchParams(search).get('src');
    if (src !== null && isCarriable(src) && localStorage.getItem(KEY) === null) {
      localStorage.setItem(KEY, src);
    }
  } catch {
    /* Safari private mode */
  }
}

export function getStashedSignupSource(): string | null {
  try {
    const stored = localStorage.getItem(KEY);
    return stored !== null && isCarriable(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Clear after a successful signup: the attribution attached, and it must not
 * mis-attach to a later account on the same device — the referral stash
 * learned this the hard way (Codex P2 on #819).
 */
export function clearStashedSignupSource(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The website A/B variant a visitor arrived through (?v=…, appended by the
 * games host's brand bar and the comic's share pages). Same shape of rule as
 * the source: first touch wins, stashed so it survives a bounce through
 * /login, sent with the registration and stored as users.signup_variant.
 * Never an allowlist — experiments are created in the admin — only a shape.
 */
const VARIANT_KEY = 'signup_variant_hint';
// ONE contract, three copies that must agree (Codex #866): this regex, the
// server's `SignupVariant::FORMAT`, and `users.signup_variant` (varchar(100),
// FORMAT's longest value is 97). The site emits `<experiment>.<variant>`; a
// format without the dot silently dropped every attribution.
const VARIANT_FORMAT = /^[a-z0-9][a-z0-9-]{0,47}\.[a-z0-9][a-z0-9-]{0,47}$/;

export function stashSignupVariantFromUrl(search: string): void {
  try {
    const v = new URLSearchParams(search).get('v');
    if (v === null || !VARIANT_FORMAT.test(v)) return;
    // First touch wins — but a hint stored under the older bare format (no
    // experiment half) is not a touch anything can be attributed to, so it
    // must not block the one that can (Codex #866).
    const kept = localStorage.getItem(VARIANT_KEY);
    if (kept === null || !VARIANT_FORMAT.test(kept)) {
      localStorage.setItem(VARIANT_KEY, v);
    }
  } catch {
    /* Safari private mode */
  }
}

/**
 * Cleared at the same chokepoints as the source (an account exists now, or
 * the visitor is authenticated): a variant seen once must not be attached to
 * every later registration from this browser (review 2026-08-27).
 */
export function clearStashedSignupVariant(): void {
  try {
    localStorage.removeItem(VARIANT_KEY);
  } catch {
    /* ignore */
  }
}

export function getStashedSignupVariant(): string | null {
  try {
    const stored = localStorage.getItem(VARIANT_KEY);
    if (stored !== null && !VARIANT_FORMAT.test(stored)) {
      localStorage.removeItem(VARIANT_KEY); // evict a legacy/garbage hint rather than carry it forever
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}
